import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  Link,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import useApi from '../../hooks/useApi';
import { useTenant } from '../../contexts/TenantContext';
import { Pagination, SearchTextField, TokenUsageBar, useErrorToast } from '../../components/common';
import { getApiErrorMessage } from '../../utils/errorMessages';
import { fetchAiUsageOverview } from '../../services/aiGovernanceService';
import {
  USAGE_SEVERITY_COLORS,
  formatCost,
  formatTokensCompact,
  getUsageSeverity,
} from '../../constants/aiUsage';
import ClientAiUsageDialog from '../Clients/ClientAiUsageDialog';

const ITEMS_PER_PAGE = 10;

/**
 * Cross-client AI usage for the JLL master tenant.
 *
 * Ordered worst-first by the API, so the clients closest to (or past) their
 * allowance are the ones on screen. Selecting a row opens the same dialog the
 * Clients module uses, so raising a limit or resetting a cycle is one click from
 * spotting the problem.
 */
const AiUsageOverviewPage = () => {
  const api = useApi();
  const { isJLLUser, hasRole } = useTenant();
  const { showErrorToast } = useErrorToast();

  const [clients, setClients] = useState([]);
  const [totals, setTotals] = useState({ totalCost: 0, totalInputTokens: 0, totalOutputTokens: 0 });
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, totalItems: 0 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [configuredOnly, setConfiguredOnly] = useState(true);
  const [loading, setLoading] = useState(true);
  const [usageDialog, setUsageDialog] = useState({ open: false, client: null });

  const canView = isJLLUser() && hasRole('JLL Super Admin');

  // ProtectedRoute's requireJLLUser also admits JLL Analyst, so explain the
  // refusal rather than rendering a blank page (same pattern as ClientsPage).
  useEffect(() => {
    if (!canView) {
      showErrorToast('Access denied. Only JLL Super Admin can view cross-client AI usage.');
    }
  }, [canView, showErrorToast]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchAiUsageOverview(api, {
        page,
        pageSize: ITEMS_PER_PAGE,
        search,
        configuredOnly,
      });
      setClients(result.clients);
      setTotals(result.totals);
      setPagination(result.pagination);
    } catch (err) {
      console.error('Error loading client AI usage:', err);
      showErrorToast(getApiErrorMessage(err, 'Failed to load client AI usage'));
    } finally {
      setLoading(false);
    }
  }, [api, page, search, configuredOnly, showErrorToast]);

  useEffect(() => {
    if (canView) {
      load();
    }
  }, [canView, load]);

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
  };

  if (!canView) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        minHeight: 'calc(100vh - 80px)',
        p: { xs: 2, sm: 2.5, md: 3 },
        margin: 0,
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}
        >
          Client AI Usage
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Token consumption and overage cost per client, highest usage first.
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <SearchTextField
            value={search}
            onChange={(event) => handleSearchChange(event.target.value)}
            onClear={() => handleSearchChange('')}
            placeholder="Search clients"
            aria-label="Search clients"
            fullWidth={false}
            sx={{ width: { xs: '100%', sm: 260 } }}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={configuredOnly}
                onChange={(event) => {
                  setConfiguredOnly(event.target.checked);
                  setPage(1);
                }}
              />
            }
            label={<Typography variant="body2">With limits only</Typography>}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: { xs: 2, sm: 4 }, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Overage cost (this page)
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {formatCost(totals.totalCost)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Tokens (in / out)
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {formatTokensCompact(totals.totalInputTokens)} / {formatTokensCompact(totals.totalOutputTokens)}
            </Typography>
          </Box>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        elevation={1}
        sx={{ borderRadius: '8px', overflow: 'auto', maxWidth: '100%' }}
      >
        <Table sx={{ minWidth: 860 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Input tokens</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Output tokens</TableCell>
              <TableCell sx={{ fontWeight: 600, display: { xs: 'none', lg: 'table-cell' } }} align="center">
                Enforcement
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">
                Overage cost
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : clients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {configuredOnly
                      ? 'No clients have AI token limits set yet.'
                      : 'No clients found.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              clients.map((client) => {
                const severity = getUsageSeverity(client.peakUsagePercent, client.alertThresholdPercent);
                const severityColor = USAGE_SEVERITY_COLORS[severity];

                return (
                  <TableRow key={client.tenantId} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip arrow title={severityColor.label}>
                          <Box
                            component="span"
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: client.isConfigured ? severityColor.main : '#bdbdbd',
                              flexShrink: 0,
                            }}
                          />
                        </Tooltip>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {client.tenantName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {client.tenantCode}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      {client.isConfigured ? (
                        <TokenUsageBar
                          label="Input"
                          used={client.inputTokensUsed}
                          limit={client.inputTokenLimit}
                          percent={client.inputUsagePercent}
                          alertThresholdPercent={client.alertThresholdPercent}
                          compact
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No limit set
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      {client.isConfigured ? (
                        <TokenUsageBar
                          label="Output"
                          used={client.outputTokensUsed}
                          limit={client.outputTokenLimit}
                          percent={client.outputUsagePercent}
                          alertThresholdPercent={client.alertThresholdPercent}
                          compact
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No limit set
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }} align="center">
                      <Chip
                        label={client.isEnforced ? 'Blocks at limit' : 'Overage allowed'}
                        size="small"
                        variant="outlined"
                        color={client.isEnforced ? 'default' : 'primary'}
                      />
                    </TableCell>

                    <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {formatCost(client.totalCost, client.currency)}
                    </TableCell>

                    <TableCell align="right">
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() =>
                          setUsageDialog({
                            open: true,
                            // The dialog speaks the Clients module's shape
                            client: {
                              TenantId: client.tenantId,
                              TenantName: client.tenantName,
                              TenantCode: client.tenantCode,
                            },
                          })
                        }
                        sx={{
                          cursor: 'pointer',
                          textDecoration: 'none',
                          color: '#1976d2',
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        Manage
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Pagination
          page={page}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          pageSize={ITEMS_PER_PAGE}
          onChange={setPage}
        />
      </Box>

      <ClientAiUsageDialog
        open={usageDialog.open}
        client={usageDialog.client}
        onClose={() => setUsageDialog({ open: false, client: null })}
        onSaved={load}
      />
    </Box>
  );
};

export default AiUsageOverviewPage;
